import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { API_HOST } from '../../utils/api';
import { useToast } from '../shared/Toast';
import { useAuth } from '../../hooks/useAuth';
import { HiOutlineLockClosed } from 'react-icons/hi';
import {
  HiOutlineDocumentText, HiOutlineMail, HiOutlineCollection, HiOutlineDocument,
  HiOutlinePhotograph, HiOutlineGlobe, HiOutlinePresentationChartBar, HiOutlineDownload, HiOutlineTrash,
  HiOutlineLightBulb, HiOutlinePhone, HiOutlineCalendar, HiOutlineChartBar
} from 'react-icons/hi';

const ASSET_ICONS = {
  blog: HiOutlineDocumentText,
  newsletter: HiOutlineMail,
  email_sequence: HiOutlineCollection,
  one_pager: HiOutlineDocument,
  banner: HiOutlinePhotograph,
  service_page: HiOutlineGlobe,
  presentation: HiOutlinePresentationChartBar,
  marketing_strategy: HiOutlineChartBar,
};

const ASSET_LABELS = {
  blog: 'Blog Post',
  newsletter: 'Newsletter',
  email_sequence: 'Email Sequence',
  one_pager: 'One-Pager',
  banner: 'Banner',
  service_page: 'Service Page',
  presentation: 'Presentation',
  marketing_strategy: 'Marketing Strategy',
};

export default function PartnerDashboard() {
  const [partner, setPartner] = useState(null);
  const [limits, setLimits] = useState([]);
  const [recentAssets, setRecentAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [showAccessNotice, setShowAccessNotice] = useState(true);
  const [pmInfo, setPmInfo] = useState(null);
  const [showCalendly, setShowCalendly] = useState(false);
  const [requestingSessions, setRequestingSession] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    async function load() {
      try {
        const [p, l, h, pm] = await Promise.all([
          api.get('/partners/me'),
          api.get('/partners/me/limits'),
          api.get('/assets/history'),
          api.get('/partner-actions/my-pm').catch(() => ({ data: { assigned: false } }))
        ]);
        setPartner(p.data);
        setLimits(l.data);
        setRecentAssets(h.data.slice(0, 5));
        setPmInfo(pm.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleRequestStrategy = async () => {
    setRequestingSession(true);
    try {
      await api.post('/partner-actions/request-strategy-session');
      addToast('Strategy session request sent to your Partner Manager!');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to send request', 'error');
    } finally {
      setRequestingSession(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      {/* Provisional (auto-provisioned) accounts: locked access notice */}
      {user?.provisional && showAccessNotice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <HiOutlineLockClosed className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Access is limited</h2>
            <p className="text-sm text-gray-600 mb-6">If you want proper access, please speak with your Partner Account Manager.</p>
            <button onClick={() => {
                setShowAccessNotice(false);
                // Let the assigned Partner Manager know this partner wants access.
                api.post('/partner-actions/access-interest').catch(() => {});
              }}
              className="px-5 py-2.5 bg-v2-blue text-white rounded-lg font-medium">Got it</button>
          </div>
        </div>
      )}

      <h1 className="text-2xl font-bold text-gray-900 mb-1">
        Welcome back{partner?.companyName ? `, ${partner.companyName}` : ''}
      </h1>
      <p className="text-gray-500 mb-8">Generate professional marketing assets for your business.</p>

      {/* Partner Manager contact section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {/* Marketing Strategy Session */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <HiOutlineLightBulb className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Marketing Strategy Session</h3>
              <p className="text-sm text-gray-500">
                {pmInfo?.assigned
                  ? `Get expert guidance from ${pmInfo.name}`
                  : 'Get expert guidance from the V2 Cloud team'}
              </p>
            </div>
          </div>
          <button
            onClick={handleRequestStrategy}
            disabled={requestingSessions}
            className="w-full mt-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            {requestingSessions ? 'Sending...' : 'Request Strategy Session'}
          </button>
          {!pmInfo?.assigned && (
            <p className="text-xs text-gray-400 mt-2">No dedicated Partner Manager assigned yet — your request will be reviewed by the V2 Cloud team.</p>
          )}
        </div>

        {/* Schedule a Call */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <HiOutlinePhone className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Schedule a Call</h3>
              <p className="text-sm text-gray-500">
                {pmInfo?.assigned ? `Book time with ${pmInfo.name}` : 'Book time with the V2 Cloud team'}
              </p>
            </div>
          </div>
          {pmInfo?.calendlyUrl ? (
            <>
              <button
                onClick={() => setShowCalendly(!showCalendly)}
                className="w-full mt-2 px-4 py-2.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <HiOutlineCalendar className="w-5 h-5" />
                {showCalendly ? 'Hide Calendar' : 'Open Calendar'}
              </button>
              {showCalendly && (
                <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
                  <iframe
                    src={pmInfo.calendlyUrl}
                    style={{ minWidth: '100%', height: '650px' }}
                    frameBorder="0"
                    title="Schedule a call"
                  />
                </div>
              )}
            </>
          ) : (
            <button
              onClick={async () => {
                setRequestingSession(true);
                try {
                  const { data } = await api.post('/partner-actions/request-call');
                  addToast(data.message || 'Call request submitted!');
                } catch (err) {
                  addToast(err.response?.data?.error || 'Failed to send request', 'error');
                } finally {
                  setRequestingSession(false);
                }
              }}
              disabled={requestingSessions}
              className="w-full mt-2 px-4 py-2.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              <HiOutlinePhone className="w-5 h-5" />
              {requestingSessions ? 'Sending...' : 'Request a Callback'}
            </button>
          )}
        </div>
      </div>

      {/* Limits overview */}
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Monthly Usage</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
        {Object.keys(ASSET_LABELS).map(type => {
          const limit = limits.find(l => l.assetType === type);
          const used = limit?.usedThisMonth || 0;
          const max = limit?.monthlyLimit || 0;
          return (
            <div key={type} className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-500">{ASSET_LABELS[type]}</div>
              <div className="text-lg font-bold text-gray-900">{used}/{max}</div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                <div
                  className={`h-1.5 rounded-full ${used >= max ? 'bg-red-500' : 'bg-v2-blue'}`}
                  style={{ width: `${max > 0 ? Math.min((used / max) * 100, 100) : 0}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick generate cards */}
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Generate an Asset</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {Object.entries(ASSET_LABELS).map(([type, label]) => {
          const Icon = ASSET_ICONS[type];
          const isStrategy = type === 'marketing_strategy';
          return (
            <Link
              key={type}
              to={`/generate?type=${type}`}
              className={`rounded-xl p-6 border transition-all group ${
                isStrategy
                  ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 hover:border-amber-400 hover:shadow-md'
                  : 'bg-white border-gray-200 hover:border-v2-blue hover:shadow-md'
              }`}
            >
              <Icon className={`w-8 h-8 mb-3 ${isStrategy ? 'text-amber-600' : 'text-gray-400 group-hover:text-v2-blue'}`} />
              <div className="font-medium text-gray-900">{label}</div>
              {isStrategy && (
                <div className="text-xs text-amber-700 mt-1 font-medium">Featured</div>
              )}
            </Link>
          );
        })}
      </div>

      {/* Recent assets */}
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Generations</h2>
      {recentAssets.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          No assets generated yet. Start by generating your first asset!
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Topic</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentAssets.map(asset => (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{ASSET_LABELS[asset.assetType] || asset.assetType}</td>
                  <td className="px-4 py-3 text-sm">{asset.topic?.title}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(asset.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full font-medium ${
                      asset.status === 'completed' ? 'bg-green-100 text-green-800' :
                      asset.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {asset.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {asset.status === 'completed' && asset.filePath && (
                        <a href={`${API_HOST}/api/assets/${asset.id}/download`} className="text-v2-blue hover:underline text-sm flex items-center gap-1">
                          <HiOutlineDownload className="w-4 h-4" /> Download
                        </a>
                      )}
                      {asset.status === 'failed' && (
                        <button onClick={async () => {
                          if (!window.confirm('Remove this failed generation?')) return;
                          try {
                            await api.delete(`/assets/${asset.id}`);
                            setRecentAssets(prev => prev.filter(a => a.id !== asset.id));
                          } catch {}
                        }} className="text-gray-400 hover:text-red-600 text-sm flex items-center gap-1" title="Remove">
                          <HiOutlineTrash className="w-4 h-4" /> Remove
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
